<template>

    <div class="border rounded-lg shadow max-h-[70vh] overflow-y-auto">

        <table class="w-full text-sm text-gray-700">

            <thead class="bg-gray-50 border-b sticky top-0 z-10">
                <tr>
                    <th class="px-4 py-3 text-left">File Name</th>
                    <th class="px-4 py-3 text-left">Uploaded File</th>
                    <th class="px-4 py-3 text-left">Description</th>
                    <th class="px-4 py-3 text-right">Size</th>
                    <th class="px-4 py-3 text-left">Created</th>
                    <th class="px-4 py-3 text-left">Status</th>
                    <th class="px-4 py-3 text-center">Actions</th>
                </tr>
            </thead>

            <tbody>
                <tr v-for="file in fileList" :key="file.id" class="border-b hover:bg-gray-50"
                    :class="{ 'animate-highlight-row': animating }">
                    <!-- File Name -->
                    <td class="px-4 py-2 max-w-[150px] truncate" :title="displayFilename(file)">
                        {{ displayFilename(file) }}
                    </td>

                    <!-- Original Filename -->
                    <td class="px-4 py-2 max-w-[150px] truncate text-gray-500" :title="file.original_filename">
                        {{ file.original_filename }}
                    </td>

                    <!-- Description -->
                    <td class="px-4 py-2 max-w-[200px] truncate" :title="file.description">
                        {{ file.description }}
                    </td>

                    <!-- Size -->
                    <td class="px-4 py-2 text-right max-w-[80px] truncate">
                        {{ file.file_size }}
                    </td>

                    <!-- Date -->
                    <td class="px-4 py-2 max-w-[120px] truncate">
                        {{ formatShortDate(new Date(file.timestamp).toLocaleString()) }}
                    </td>

                    <!-- Status -->
                    <td class="px-4 py-2 max-w-[80px] truncate capitalize">
                        {{ file.status }}
                    </td>

                    <!-- Actions -->
                    <td class="px-4 py-2 flex justify-center gap-3">

                        <!-- Download -->
                        <button @click="confirmDownload(file)" class="p-1 rounded hover:bg-gray-100"
                            :title="'Download '">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"
                                class="w-5 h-5 text-blue-600">
                                <path
                                    d="M13 16.17L18.36 10.81L19.78 12.22L12 20L4.22 12.22L5.64 10.81L11 16.17V4H13V16.17Z" />
                            </svg>
                        </button>

                        <!-- Edit -->
                        <button @click="$emit('edit', file)" class="p-1 rounded hover:bg-gray-100" :title="'Edit '">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"
                                class="w-5 h-5 text-orange-500">
                                <path
                                    d="M3 17.25V21h3.75l11-11.03-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z" />
                            </svg>
                        </button>

                    </td>
                </tr>
            </tbody>

            <!-- Pagination Footer -->
            <tfoot class="bg-gray-50 border-t">
                <tr>
                    <td colspan="8">
                        <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-4 py-2">

                            <!-- Rows per page -->
                            <div class="flex items-center gap-2 text-sm text-gray-600">
                                <span>Rows per page</span>
                                <select :value="pageSize" @change="onChangePageSize($event.target.value)"
                                    class="border rounded px-2 py-1 text-sm bg-white">
                                    <option :value="5">5</option>
                                    <option :value="10">10</option>
                                    <option :value="20">20</option>
                                    <option :value="50">50</option>
                                </select>
                            </div>

                            <!-- Page info -->
                            <div class="text-sm text-gray-600 text-center">
                                Page {{ currentPage }} of {{ totalPages }}
                                <span class="ml-2 text-gray-400">
                                    ({{ totalItems }} files)
                                </span>
                            </div>

                            <!-- Prev / Next -->
                            <div class="flex items-center gap-2">
                                <button @click="$emit('prev-page')" :disabled="currentPage === 1"
                                    class="px-3 py-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-40">
                                    Prev
                                </button>
                                <button @click="$emit('next-page')" :disabled="currentPage === totalPages"
                                    class="px-3 py-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-40">
                                    Next
                                </button>
                            </div>

                        </div>
                    </td>
                </tr>
            </tfoot>

        </table>

        <!-- Confirmation Modal -->
        <ConfirmationModal :show="showConfirm" title="Confirm Action" :message="modalMessage" confirmText="Yes"
            cancelText="Cancel" @confirm="handleConfirm" @cancel="showConfirm = false" />

    </div>
</template>

<script>

import { ref, computed } from "vue";
import { formatShortDate } from "@/utils/dateFormat";
import ConfirmationModal from "@/components/modals/ConfirmationModal.vue";

export default {
    name: "DocumentFileListsCard",
    components: { ConfirmationModal },

    props: {
        fileList: { type: Array, required: true },
        pageSize: { type: Number, required: true },
        selectedIds: { type: Array, required: true },
        totalPages: { type: Number, required: true },
        totalItems: { type: Number, required: true },
        currentPage: { type: Number, required: true },
    },

    emits: [
        "edit",
        "download",
        "toggle-select",
        "toggle-status",
        "next-page",
        "prev-page",
        "change-page-size",
    ],

    setup(props, { emit }) {
        const showConfirm = ref(false);
        const confirmAction = ref(null);
        const animating = ref(false);
        const fileToAction = ref(null);

        const displayFilename = (file) => file.filename || file.original_filename || "Unnamed";

        const modalMessage = computed(() => {
            if (!fileToAction.value) return "";
            if (confirmAction.value === "toggle") {
                return `Are you sure you want to ${fileToAction.value.status === "active" ? "deactivate" : "activate"} this file?`;
            }
            if (confirmAction.value === "download") {
                return `Download "${displayFilename(fileToAction.value)}"?`;
            }
            return "";
        });

        const confirmToggleStatus = (file) => {
            fileToAction.value = file;
            confirmAction.value = "toggle";
            showConfirm.value = true;
        };

        const confirmDownload = (file) => {
            fileToAction.value = file;
            confirmAction.value = "download";
            showConfirm.value = true;
        };

        const handleConfirm = () => {
            showConfirm.value = false;
            animating.value = true;

            if (!fileToAction.value) return;

            emit(confirmAction.value === "toggle" ? "toggle-status" : "download", fileToAction.value);

            setTimeout(() => (animating.value = false), 400);
            fileToAction.value = null;
            confirmAction.value = null;
        };

        const toggleSelect = (file) => emit("toggle-select", file);

        const onChangePageSize = (newSize) => {
            emit("change-page-size", Number(newSize));
        };

        return {
            displayFilename,
            confirmToggleStatus,
            confirmDownload,
            handleConfirm,
            toggleSelect,
            onChangePageSize,
            formatShortDate,

            animating,
            showConfirm,
            modalMessage,
        };
    },
};

</script>

<style scoped>
@keyframes highlight-row {
    0% {
        background-color: #fff;
    }

    50% {
        background-color: #fef9c3;
    }

    100% {
        background-color: #fff;
    }
}

.animate-highlight-row {
    animation: highlight-row 0.4s ease-in-out;
}
</style>